/// FOURJS_START_COPYRIGHT(D,2026)
/// Property of Four Js*
/// (c) Copyright Four Js 2026, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT
"use strict";

/**
 * @typedef {Object} FilterableListItemData
 * @property {string|number} id - Unique identifier
 * @property {string} label - Display text
 * @property {?string} [group] - Reorder group: items only reorder within their group, a separator is drawn between groups
 * @property {FilterableListItemState} data - Item state
 */

/**
 * @typedef {Object} FilterableListItemState
 * @property {boolean} [visible=true] - Whether the item is visible
 * @property {boolean} [movable=true] - Whether the item can be reordered
 * @property {boolean} [hidable=true] - Whether visibility can be toggled
 * @property {string} [stateText] - Extra state text appended to the screen reader state
 */

modulum("FilterableListWidget", ["WidgetBase"], function (context, cls) {
  /**
   * Generic searchable, filterable and reorderable list widget.
   *
   * This component is designed to be embedded inside container widgets
   * (e.g. SidePanelWidget) and driven by a consumer that provides data.
   * Each item manages its own visual state and emits action events.
   *
   * Items are stored in a single ordered array of FilterableListItemWidget.
   * The array order is the visual order. Badges reflect position (index + 1).
   *
   * @class FilterableListWidget
   * @memberOf classes
   * @extends classes.WidgetBase
   */
  cls.FilterableListWidget = context.oo.Class(cls.WidgetBase, function ($super) {
    return /** @lends classes.FilterableListWidget.prototype */ {
      __name: "FilterableListWidget",

      /**
       * Ordered array of item widgets. Array order = visual order.
       * @type {Array<classes.FilterableListItemWidget>}
       */
      _itemWidgets: null,

      /**
       * DOM: "Show all" button element
       * @type {HTMLElement|null}
       */
      _showAllButtonElement: null,

      /**
       * DOM: filter toggle button element
       * @type {HTMLElement|null}
       */
      _filterToggleElement: null,

      /**
       * DOM: search input element
       * @type {HTMLInputElement|null}
       */
      _searchInputElement: null,

      /**
       * DOM: search clear button element
       * @type {HTMLElement|null}
       */
      _searchClearElement: null,

      /**
       * DOM: list container element
       * @type {HTMLElement|null}
       */
      _listContainerElement: null,

      /**
       * DOM: list title element
       * @type {HTMLElement|null}
       */
      _titleElement: null,

      /**
       * DOM: "…" menu button element
       * @type {HTMLElement|null}
       */
      _menuButtonElement: null,

      /**
       * Consumer-provided "…" menu actions
       * @type {?Array<{label: string, callback: Function, isVisible: ?Function}>}
       */
      _menuActions: null,

      /**
       * The "…" menu currently displayed
       * @type {?classes.ContextMenuWidget}
       */
      _menu: null,

      /**
       * DOM: filters area element
       * @type {HTMLElement|null}
       */
      _filtersAreaElement: null,

      /**
       * State filter checkboxes, keyed by filter name
       * @type {?Object<string, classes.CheckBoxWidget>}
       */
      _filterCheckBoxes: null,

      /**
       * The position badges checkbox
       * @type {classes.CheckBoxWidget|null}
       */
      _badgesCheckBox: null,

      /**
       * Active state filters. An item is displayed when at least one of its states is checked.
       * @type {?{visible: boolean, hidden: boolean, unmovable: boolean}}
       */
      _activeFilters: null,

      /**
       * Currently dragged item widget (during DnD)
       * @type {?classes.FilterableListItemWidget}
       */
      _draggedItem: null,

      /**
       * Currently hovered item widget (during DnD)
       * @type {?classes.FilterableListItemWidget}
       */
      _hoveredItem: null,

      /**
       * Whether the drop indicator is above (true) or below (false) the hovered item
       * @type {boolean}
       */
      _dropAbove: false,

      /**
       * Bound pointer event handlers for DnD
       * @type {Function|null}
       */
      _boundOnPointerDown: null,
      _boundOnPointerMove: null,
      _boundOnPointerUp: null,

      /**
       * Bound handlers for top actions
       * @type {Function|null}
       */
      _boundOnSearchInput: null,

      /**
       * Bound focus handler for the list (roving tab stop seeding)
       * @type {Function|null}
       */
      _boundOnListFocusIn: null,

      /**
       * Bound blur handler for the list (cancels a pending position edit)
       * @type {Function|null}
       */
      _boundOnListFocusOut: null,

      /**
       * Id of the item holding the roving tab stop — the whole list is a single Tab stop
       * @type {?string}
       */
      _rovingItemId: null,

      /**
       * The item whose position input is currently edited
       * @type {?classes.FilterableListItemWidget}
       */
      _editingItemWidget: null,

      /**
       * Position input value before the edit, restored on cancel
       * @type {string}
       */
      _editOriginalValue: "",

      /**
       * Tooltip displayed on the group separators
       * @type {?string}
       */
      _groupSeparatorTitle: null,

      /**
       * DOM: visually hidden aria-live region announcing action results to screen readers
       * @type {HTMLElement|null}
       */
      _liveRegionElement: null,

      /**
       * DOM: visually hidden assertive aria-live region for the position edit outcomes
       * @type {HTMLElement|null}
       */
      _assertiveLiveRegionElement: null,

      /**
       * DOM: visually hidden keyboard instructions, referenced by the rows aria-describedby
       * @type {HTMLElement|null}
       */
      _instructionsElement: null,

      /**
       * True while the grid navigation moves the focus — a badge focused this way must not enter edit mode
       * @type {boolean}
       */
      _gridNavFocus: false,

      /**
       * @inheritDoc
       */
      constructor: function (opts) {
        this._itemWidgets = [];
        this._activeFilters = { visible: true, hidden: true, unmovable: true };
        $super.constructor.call(this, opts);
      },

      /**
       * @inheritDoc
       */
      _initElement: function () {
        $super._initElement.call(this);
        this._showAllButtonElement = this._element.getElementsByClassName("gbc_FilterableListWidget__showAllButton")[0];
        this._filterToggleElement = this._element.getElementsByClassName("gbc_FilterableListWidget__filterToggle")[0];
        this._searchInputElement = this._element.getElementsByClassName("gbc_FilterableListWidget__searchInput")[0];
        this._searchClearElement = this._element.getElementsByClassName("gbc_FilterableListWidget__searchClear")[0];
        this._listContainerElement = this._element.getElementsByClassName("gbc_FilterableListWidget__list")[0];
        this._titleElement = this._element.getElementsByClassName("gbc_FilterableListWidget__title")[0];
        this._menuButtonElement = this._element.getElementsByClassName("gbc_FilterableListWidget__menuButton")[0];
        this._filtersAreaElement = this._element.getElementsByClassName("gbc_FilterableListWidget__filters")[0];

        this._showAllButtonElement.textContent = gbc.I18NService.t("gwc.filterableList.showAll");
        this._searchInputElement.placeholder = gbc.I18NService.t("gwc.filterableList.searchPlaceholder");
        this._searchInputElement.setAttribute("aria-label", gbc.I18NService.t("gwc.filterableList.searchPlaceholder"));
        this._filterToggleElement.setAttribute("aria-label", gbc.I18NService.t("gwc.filterableList.filters"));
        this._menuButtonElement.setAttribute("aria-label", gbc.I18NService.t("gwc.filterableList.moreOptions"));

        const onStateFilterChanged = this._onStateFilterChanged.bind(this);
        this._filterCheckBoxes = {
          visible: this._buildFilterCheckBox(gbc.I18NService.t("gwc.filterableList.filterVisible"), onStateFilterChanged),
          hidden: this._buildFilterCheckBox(gbc.I18NService.t("gwc.filterableList.filterHidden"), onStateFilterChanged),
          unmovable: this._buildFilterCheckBox(gbc.I18NService.t("gwc.filterableList.filterUnmovable"), onStateFilterChanged)
        };
        this._badgesCheckBox = this._buildFilterCheckBox(
          gbc.I18NService.t("gwc.filterableList.filterColumnNumber"),
          function () {
            this.setBadgesVisible(Boolean(this._badgesCheckBox.getValue()));
          }.bind(this)
        );

        this._boundOnSearchInput = this._onSearchInput.bind(this);
        this._searchInputElement.addEventListener("input", this._boundOnSearchInput);

        this._boundOnPointerDown = this._onPointerDown.bind(this);
        this._boundOnPointerMove = this._onPointerMove.bind(this);
        this._boundOnPointerUp = this._onPointerUp.bind(this);
        this._listContainerElement.addEventListener("pointerdown", this._boundOnPointerDown);

        this._boundOnListFocusIn = this._onListFocusIn.bind(this);
        this._listContainerElement.addEventListener("focusin", this._boundOnListFocusIn);
        this._boundOnListFocusOut = this._onListFocusOut.bind(this);
        this._listContainerElement.addEventListener("focusout", this._boundOnListFocusOut);

        this._liveRegionElement = document.createElement("div");
        this._liveRegionElement.className = "gbc_FilterableListWidget__liveRegion";
        this._liveRegionElement.setAttribute("aria-live", "polite");
        this._element.appendChild(this._liveRegionElement);

        // separate region: position edit outcomes are announced assertively
        this._assertiveLiveRegionElement = document.createElement("div");
        this._assertiveLiveRegionElement.className = "gbc_FilterableListWidget__liveRegion gbc_FilterableListWidget__liveRegionAssertive";
        this._assertiveLiveRegionElement.setAttribute("aria-live", "assertive");
        this._element.appendChild(this._assertiveLiveRegionElement);

        this._instructionsElement = document.createElement("span");
        this._instructionsElement.id = `w_${this.getUniqueIdentifier()}_listInstructions`;
        this._instructionsElement.className = "gbc_FilterableListWidget__instructions";
        this._instructionsElement.textContent = gbc.I18NService.t("gwc.filterableList.rowInstructions");
        this._element.appendChild(this._instructionsElement);
      },

      /**
       * Announce a message to screen readers through the list live regions
       * @param {string} text
       * @param {boolean} [assertive] - true to interrupt the current screen reader speech
       */
      announce: function (text, assertive) {
        const region = assertive ? this._assertiveLiveRegionElement : this._liveRegionElement;
        if (!region) {
          return;
        }
        // cleared first so the same message announced twice in a row is read again
        region.textContent = "";
        this.afterDomMutator(
          function () {
            if (!this.isDestroyed()) {
              region.textContent = text;
            }
          }.bind(this)
        );
      },

      /**
       * @inheritDoc
       */
      manageMouseClick: function (domEvent) {
        const target = domEvent.target;
        if (target.closest(".gbc_FilterableListWidget__showAllButton")) {
          this._onShowAll();
        } else if (target.closest(".gbc_FilterableListWidget__filterToggle")) {
          this._onFilterToggle();
        } else if (target.closest(".gbc_FilterableListWidget__searchClear")) {
          this._onSearchClear();
        } else if (target.closest(".gbc_FilterableListWidget__menuButton")) {
          this._openMenu();
        }
        return $super.manageMouseClick.call(this, domEvent);
      },

      /**
       * @inheritDoc
       */
      managePriorityKeyDown: function (keyString, domKeyEvent, repeat) {
        // the "…" menu is a dropdown owned by this widget: while it is open the keys are
        // routed to it, as every dropdown-owning widget does (the focus stays on the button)
        if (this._menu?.managePriorityKeyDown(keyString, domKeyEvent, repeat)) {
          return true;
        }
        return $super.managePriorityKeyDown.call(this, keyString, domKeyEvent, repeat);
      },

      /**
       * @inheritDoc
       */
      destroy: function () {
        this._cleanupDrag();
        this._endPositionEdit();
        if (this._menu) {
          this._menu.destroyChildren();
          this._menu.destroy();
          this._menu = null;
        }
        if (this._filterCheckBoxes) {
          for (const key of Object.keys(this._filterCheckBoxes)) {
            this._filterCheckBoxes[key].destroy();
          }
          this._filterCheckBoxes = null;
        }
        if (this._badgesCheckBox) {
          this._badgesCheckBox.destroy();
          this._badgesCheckBox = null;
        }
        this._menuActions = null;
        this._activeFilters = null;
        if (this._listContainerElement) {
          if (this._boundOnPointerDown) {
            this._listContainerElement.removeEventListener("pointerdown", this._boundOnPointerDown);
          }
          if (this._boundOnListFocusIn) {
            this._listContainerElement.removeEventListener("focusin", this._boundOnListFocusIn);
          }
          if (this._boundOnListFocusOut) {
            this._listContainerElement.removeEventListener("focusout", this._boundOnListFocusOut);
          }
        }
        if (this._searchInputElement && this._boundOnSearchInput) {
          this._searchInputElement.removeEventListener("input", this._boundOnSearchInput);
        }
        this._boundOnPointerDown = null;
        this._boundOnPointerMove = null;
        this._boundOnPointerUp = null;
        this._boundOnSearchInput = null;
        this._boundOnListFocusIn = null;
        this._boundOnListFocusOut = null;
        this._rovingItemId = null;
        this._liveRegionElement = null;
        this._assertiveLiveRegionElement = null;
        this._instructionsElement = null;
        this._groupSeparatorTitle = null;

        if (this._itemWidgets) {
          for (const itemWidget of this._itemWidgets) {
            itemWidget.destroy();
          }
          this._itemWidgets = null;
        }

        this._showAllButtonElement = null;
        this._filterToggleElement = null;
        this._searchInputElement = null;
        this._searchClearElement = null;
        this._listContainerElement = null;
        this._titleElement = null;
        this._menuButtonElement = null;
        this._filtersAreaElement = null;
        this._draggedItem = null;
        this._hoveredItem = null;
        $super.destroy.call(this);
      },

      /**
       * Set the full list of items, replacing any existing items.
       * Badges are automatically set to position index + 1.
       * @param {Array<FilterableListItemData>} items
       */
      setItems: function (items) {
        // The rebuild destroys the rows: remember the keyboard position to restore it afterwards
        // (contains: the focus may sit inside the row, e.g. in the position input)
        const focusedElement = document.activeElement;
        const focusedItem = this._itemWidgets.find((widget) => widget.getElement().contains(focusedElement));
        const focusedId = focusedItem ? focusedItem.getItemData().id : null;
        // the focus goes back to the same row stop: the row itself, its badge or its visibility button
        let focusedStop = null;
        if (focusedItem) {
          if (focusedElement === focusedItem.getBadgeElement()) {
            focusedStop = "badge";
          } else if (focusedElement === focusedItem.getVisibilityButtonElement()) {
            focusedStop = "visibility";
          }
        }

        this._clearAllItems();
        items = items || [];
        for (let i = 0; i < items.length; i++) {
          this._createItemWidget(items[i], i);
        }
        // Rebuilt items are unfiltered — reapply the current search query and state filters
        this._applyDisplayFilters();

        if (focusedId !== null) {
          this._focusItemById(focusedId);
          const rebuiltItem = focusedStop ? this.getItemWidget(focusedId) : null;
          if (rebuiltItem) {
            const stopElement = focusedStop === "badge" ? rebuiltItem.getBadgeElement() : rebuiltItem.getVisibilityButtonElement();
            if (stopElement && stopElement.offsetParent !== null) {
              this._focusRowStop(rebuiltItem, stopElement);
            }
          }
        }
      },

      /**
       * Add a single item at the end of the list
       * @param {FilterableListItemData} item
       */
      addItem: function (item) {
        this._createItemWidget(item, this._itemWidgets.length);
        this._updateRovingTabStop();
        this._updateGroupSeparators();
      },

      /**
       * Remove an item by its id. Re-numbers badges after removal.
       * @param {string} id
       */
      removeItem: function (id) {
        const index = this._findIndexById(id);
        if (index === -1) {
          return;
        }
        const itemWidget = this._itemWidgets[index];
        if (itemWidget === this._draggedItem || itemWidget === this._hoveredItem) {
          this._cleanupDrag();
        }
        if (itemWidget === this._editingItemWidget) {
          this._endPositionEdit();
        }
        itemWidget.getElement().remove();
        itemWidget.destroy();
        this._itemWidgets.splice(index, 1);
        this._refreshBadges();
        this._updateRovingTabStop();
        this._updateGroupSeparators();
      },

      /**
       * Partially update an existing item's data
       * @param {string} id
       * @param {Partial<FilterableListItemState>} partial - properties to merge into item.data
       */
      updateItem: function (id, partial) {
        const itemWidget = this.getItemWidget(id);
        if (!itemWidget) {
          return;
        }
        const itemData = itemWidget.getItemData();
        Object.assign(itemData.data, partial);
        itemWidget.setItemData(itemData);
      },

      /**
       * Returns item data objects in their current visual order
       * @returns {Array<FilterableListItemData>}
       */
      getItems: function () {
        if (!this._itemWidgets) {
          return [];
        }
        return this._itemWidgets.map((widget) => widget.getItemData());
      },

      /**
       * Returns item IDs in their current visual order
       * @returns {Array<string>}
       */
      getOrderedIds: function () {
        if (!this._itemWidgets) {
          return [];
        }
        return this._itemWidgets.map((widget) => widget.getItemData().id);
      },

      /**
       * Find an item widget by its id
       * @param {string} id
       * @returns {?classes.FilterableListItemWidget}
       */
      getItemWidget: function (id) {
        const index = this._findIndexById(id);
        if (index === -1) {
          return null;
        }
        return this._itemWidgets[index];
      },

      // ── Top actions (internal) ──────────────────────────────────────────────

      /**
       * Emit show all event
       * @private
       */
      _onShowAll: function () {
        this.emit(context.constants.widgetEvents.listShowAll);
      },

      /**
       * Show or hide the filters area
       * @private
       */
      _onFilterToggle: function () {
        const isVisible = this._filtersAreaElement.classList.toggle("visible");
        this._filterToggleElement.classList.toggle("active", isVisible);
        this._filterToggleElement.setAttribute("aria-expanded", String(isVisible));
      },

      /**
       * Build one filter checkbox and append it to the filters area
       * @param {string} label - displayed text
       * @param {Function} onChange - fired when the checkbox value changes
       * @returns {classes.CheckBoxWidget}
       * @private
       */
      _buildFilterCheckBox: function (label, onChange) {
        /** @type {classes.CheckBoxWidget} */
        const check = cls.WidgetFactory.createWidget("CheckBox", this.getBuildParameters());
        check.setParentWidget(this);
        check.setEnabled(true);
        check.setText(label);
        check.setValue(true);
        check.when(context.constants.widgetEvents.valueChanged, onChange);
        this._filtersAreaElement.appendChild(check.getElement());
        return check;
      },

      /**
       * Sync the active state filters from the checkboxes and refresh the list
       * @private
       */
      _onStateFilterChanged: function () {
        this._activeFilters.visible = Boolean(this._filterCheckBoxes.visible.getValue());
        this._activeFilters.hidden = Boolean(this._filterCheckBoxes.hidden.getValue());
        this._activeFilters.unmovable = Boolean(this._filterCheckBoxes.unmovable.getValue());
        this._applyDisplayFilters();
        this._announceDisplayedCount();
      },

      /**
       * Set the list title, displayed on the top actions row. Empty text hides it.
       * @param {string} text
       */
      setTitleText: function (text) {
        if (!this._titleElement) {
          return;
        }
        this._titleElement.textContent = text;
        this._titleElement.classList.toggle("hidden", !text);
        // The title also names the grid
        cls.DOMElementHelper.updateAttribute(this._listContainerElement, "aria-label", text || null);
      },

      /**
       * Set the search input placeholder text, also used as its accessible name
       * @param {string} text
       */
      setSearchPlaceholder: function (text) {
        if (this._searchInputElement) {
          this._searchInputElement.placeholder = text;
          this._searchInputElement.setAttribute("aria-label", text);
        }
      },

      /**
       * Add an action to the "…" menu. The button appears once an action is registered.
       * @param {string} label - displayed text
       * @param {Function} callback - fired when the entry is clicked
       * @param {Function} [isVisible] - evaluated when the menu opens; return false to skip the entry
       * @param {string} [icon] - optional image source displayed before the label (e.g. "zmdi-eye-off")
       */
      addMenuAction: function (label, callback, isVisible, icon) {
        if (!this._menuActions) {
          this._menuActions = [];
        }
        this._menuActions.push({ label: label, callback: callback, isVisible: isVisible || null, icon: icon || null });
        if (this._menuButtonElement) {
          this._menuButtonElement.classList.remove("hidden");
        }
      },

      /**
       * Build and show a popup menu anchored on an element. Destroyed once closed.
       * @param {HTMLElement} anchorElement - the trigger button
       * @param {Function} fill - receives (menu, opts) and adds the menu entries
       * @param {Function} onClosed - fired once the menu is closed and destroyed
       * @returns {?classes.ContextMenuWidget} the displayed menu, or null when it has no entry
       * @private
       */
      _openPopupMenu: function (anchorElement, fill, onClosed) {
        const opts = this.getBuildParameters();
        opts.ignoreLayout = true;

        /** @type {classes.ContextMenuWidget} */
        const menu = cls.WidgetFactory.createWidget("ContextMenu", opts);
        menu.setParentWidget(this);
        // scoping class: the menu DOM lives at application level, not under this widget
        menu.addClasses("gbc_FilterableListWidget__menu");

        fill(menu, opts);

        if (menu.getChildren().length === 0) {
          menu.destroy();
          return null;
        }

        // one-shot by guard: destroying a still-open menu re-emits a close, the hook must not
        // re-enter. The handle is swept once by menu.destroy() (DropDownWidget._handlers) —
        // unregistering it here too would remove it twice
        let menuClosed = false;
        menu.onClose(function () {
          if (menuClosed) {
            return;
          }
          menuClosed = true;
          menu.destroyChildren();
          menu.destroy();
          anchorElement.setAttribute("aria-expanded", "false");
          onClosed();
        });

        menu.parentElement = anchorElement;
        menu.show();
        anchorElement.setAttribute("aria-expanded", "true");
        return menu;
      },

      /**
       * Build and show the "…" menu with the currently visible actions
       * @private
       */
      _openMenu: function () {
        if (this._menu || !this._menuActions) {
          return;
        }
        this._menu = this._openPopupMenu(
          this._menuButtonElement,
          function (menu, opts) {
            for (const action of this._menuActions) {
              if (action.isVisible && !action.isVisible()) {
                continue;
              }
              /** @type {classes.MenuLabelWidget} */
              const label = cls.WidgetFactory.createWidget("MenuLabel", opts);
              label.setValue(action.label);
              if (action.icon) {
                label.setImage(action.icon);
              }
              menu.addChildWidget(label, {
                clickCallback: action.callback
              });
            }
          }.bind(this),
          function () {
            this._menu = null;
          }.bind(this)
        );
      },

      /**
       * Show or hide the position badges on all items
       * @param {boolean} visible
       */
      setBadgesVisible: function (visible) {
        this.toggleClass("gbc_FilterableListWidget--hideBadges", !visible);
      },

      /**
       * Create a FilterableListItemWidget for the given item and append it to the list
       * @param {FilterableListItemData} item
       * @param {number} index - position in the list (used for badge)
       * @private
       */
      _createItemWidget: function (item, index) {
        const itemWidget = new cls.FilterableListItemWidget(this.getBuildParameters());
        itemWidget.setParentWidget(this);
        itemWidget.setItemData(item);
        itemWidget.setBadgeText(String(index + 1));
        itemWidget.setAriaAttribute("describedby", this._instructionsElement.id);
        // Relay item-level events to list-level events
        itemWidget.when(context.constants.widgetEvents.listItemToggleVisibility, (event, src, data) => {
          this.emit(context.constants.widgetEvents.listItemVisibility, data);
        });
        this._itemWidgets.push(itemWidget);
        this._listContainerElement.appendChild(itemWidget.getElement());
      },

      /**
       * Destroy all item widgets and clear the list container
       * @private
       */
      _clearAllItems: function () {
        // a rebuild mid-drag must drop the drag state (and its document listeners) before the rows are destroyed
        this._cleanupDrag();
        this._endPositionEdit();
        if (this._itemWidgets) {
          for (const itemWidget of this._itemWidgets) {
            itemWidget.destroy();
          }
          this._itemWidgets = [];
        }
        if (this._listContainerElement) {
          this._listContainerElement.replaceChildren();
        }
      },

      /**
       * Find the index of an item widget by its id
       * @param {string} id
       * @returns {number} index in _itemWidgets, or -1 if not found
       * @private
       */
      _findIndexById: function (id) {
        for (let i = 0; i < this._itemWidgets.length; i++) {
          if (this._itemWidgets[i].getItemData().id === id) {
            return i;
          }
        }
        return -1;
      },

      /**
       * Update all badges to reflect current position (index + 1)
       * @private
       */
      _refreshBadges: function () {
        for (let i = 0; i < this._itemWidgets.length; i++) {
          this._itemWidgets[i].setBadgeText(String(i + 1));
        }
      },

      /**
       * Set the tooltip of the group separators (e.g. "Frozen columns")
       * @param {string} title
       */
      setGroupSeparatorTitle: function (title) {
        this._groupSeparatorTitle = title;
      },

      /**
       * Redraw the separators between item groups. They are derived from the displayed
       * items: dropped and rebuilt after any reorder, filter or rebuild.
       * @private
       */
      _updateGroupSeparators: function () {
        if (!this._listContainerElement) {
          return;
        }
        for (const separator of Array.from(this._listContainerElement.querySelectorAll(".gbc_FilterableListWidget__groupSeparator"))) {
          separator.remove();
        }
        const displayed = this._getDisplayedItemWidgets();
        for (let i = 1; i < displayed.length; i++) {
          if (displayed[i].getGroup() !== displayed[i - 1].getGroup()) {
            const separator = document.createElement("li");
            separator.className = "gbc_FilterableListWidget__groupSeparator";
            // purely visual: the group is conveyed to screen readers through the item state text
            separator.setAttribute("aria-hidden", "true");
            if (this._groupSeparatorTitle) {
              separator.title = this._groupSeparatorTitle;
            }
            separator.innerHTML = `<i class="zmdi zmdi-pin-outline"></i>`;
            this._listContainerElement.insertBefore(separator, displayed[i].getElement());
          }
        }
      },

      /**
       * Handle search input changes
       * @private
       */
      _onSearchInput: function () {
        this._applyDisplayFilters();
        this._displaySearchClear();
        this._announceDisplayedCount();
      },

      /**
       * Clear the search input, reset filter and refocus
       * @private
       */
      _onSearchClear: function () {
        this._searchInputElement.value = "";
        this._applyDisplayFilters();
        this._displaySearchClear();
        this._searchInputElement.focus();
        this._announceDisplayedCount();
      },

      /**
       * Announce the number of displayed items, after a search or a filter change
       * @private
       */
      _announceDisplayedCount: function () {
        const count = this._getDisplayedItemWidgets().length;
        this.announce(gbc.I18NService.t("gwc.filterableList.resultsAnnounce").replace("{count}", String(count)));
      },

      /**
       * Show or hide the search clear button depending on input content
       * @private
       */
      _displaySearchClear: function () {
        if (this._searchClearElement) {
          this._searchClearElement.classList.toggle("visible", this._searchInputElement.value.length > 0);
        }
      },

      /**
       * Show/hide items combining the text query and the active state filters
       * @private
       */
      _applyDisplayFilters: function () {
        const query = this._searchInputElement ? this._searchInputElement.value.toLowerCase() : "";

        for (const itemWidget of this._itemWidgets) {
          const label = itemWidget.getItemData().label.toLowerCase();
          let match = !query || label.includes(query);
          if (match) {
            match =
              (this._activeFilters.visible && itemWidget.isItemVisible()) ||
              (this._activeFilters.hidden && !itemWidget.isItemVisible()) ||
              (this._activeFilters.unmovable && !itemWidget.isMovable());
          }
          // `.filtered-out` is `display:none` — drops the item from Tab + a11y tree.
          itemWidget.toggleClass("filtered-out", !match);
        }
        this._updateRovingTabStop();
        this._updateGroupSeparators();
      },

      // ── Drag-and-drop (internal) ────────────────────────────────────────────

      /**
       * Handle pointerdown on the list container.
       * Starts a drag only if the target is a drag handle of a movable item.
       * @param {PointerEvent} evt
       * @private
       */
      _onPointerDown: function (evt) {
        if (this._draggedItem) {
          return;
        }
        const handleElement = evt.target?.closest(".gbc_FilterableListItemWidget__dragHandle");
        if (!handleElement || handleElement.classList.contains("locked")) {
          return;
        }
        const itemElement = evt.target.closest(".gbc_FilterableListItemWidget");
        if (!itemElement) {
          return;
        }
        const itemWidget = this._getItemWidgetFromElement(itemElement);
        if (!itemWidget?.isMovable()) {
          return;
        }

        evt.preventDefault();
        this._draggedItem = itemWidget;
        itemWidget.addClasses("dragging");

        document.addEventListener("pointermove", this._boundOnPointerMove);
        document.addEventListener("pointerup", this._boundOnPointerUp);
      },

      /**
       * Handle pointermove during drag.
       * Finds the hovered item and shows a drop indicator above or below.
       * @param {PointerEvent} evt
       * @private
       */
      _onPointerMove: function (evt) {
        if (!this._draggedItem) {
          return;
        }
        evt.preventDefault();

        const elementUnderPointer = document.elementFromPoint(evt.clientX, evt.clientY);
        if (!elementUnderPointer) {
          this._setHoveredItem(null);
          return;
        }
        const itemElement = elementUnderPointer.closest(".gbc_FilterableListItemWidget");
        if (!itemElement) {
          this._setHoveredItem(null);
          return;
        }
        const itemWidget = this._getItemWidgetFromElement(itemElement);
        if (!itemWidget || itemWidget === this._draggedItem) {
          this._setHoveredItem(null);
          return;
        }
        // groups are reorder boundaries: an item cannot be dropped in another group
        if (itemWidget.getGroup() !== this._draggedItem.getGroup()) {
          this._setHoveredItem(null);
          return;
        }

        const rect = itemElement.getBoundingClientRect();
        const isAbove = evt.clientY < rect.top + rect.height / 2;
        this._setHoveredItem(itemWidget, isAbove);
      },

      /**
       * Handle pointerup — perform the reorder and cleanup.
       * @param {PointerEvent} evt
       * @private
       */
      _onPointerUp: function (evt) {
        if (!this._draggedItem) {
          return;
        }

        if (this._hoveredItem && this._hoveredItem !== this._draggedItem) {
          this._reorderItem(this._draggedItem, this._hoveredItem, this._dropAbove);
        }

        this._cleanupDrag();
      },

      /**
       * Move draggedWidget above or below targetWidget in the array and DOM.
       * Refreshes badges and emits the reorder event.
       * @param {classes.FilterableListItemWidget} draggedWidget
       * @param {classes.FilterableListItemWidget} targetWidget
       * @param {boolean} insertAbove - true to insert before targetWidget, false to insert after
       * @private
       */
      _reorderItem: function (draggedWidget, targetWidget, insertAbove) {
        const fromIndex = this._itemWidgets.indexOf(draggedWidget);
        const targetIndex = this._itemWidgets.indexOf(targetWidget);
        if (fromIndex === -1 || targetIndex === -1 || fromIndex === targetIndex) {
          return;
        }

        // final index: the target's index once the dragged item is out of the way
        let toIndex = targetIndex > fromIndex ? targetIndex - 1 : targetIndex;
        if (!insertAbove) {
          toIndex = toIndex + 1;
        }
        if (!this._applyReorder(draggedWidget, toIndex)) {
          return;
        }

        const displayed = this._getDisplayedItemWidgets();
        this.announce(
          gbc.I18NService.t("gwc.filterableList.itemMovedAnnounce")
            .replace("{name}", draggedWidget.getItemData().label)
            .replace("{position}", String(displayed.indexOf(draggedWidget) + 1))
            .replace("{total}", String(displayed.length))
        );
      },

      /**
       * Move an item to the given index of the items array: syncs the DOM order,
       * the badges, the group separators, and emits the reorder event.
       * @param {classes.FilterableListItemWidget} itemWidget
       * @param {number} toIndex - target index in the items array
       * @returns {boolean} true when the item actually moved
       * @private
       */
      _applyReorder: function (itemWidget, toIndex) {
        const fromIndex = this._itemWidgets.indexOf(itemWidget);
        if (fromIndex === -1 || toIndex === fromIndex) {
          return false;
        }
        this._itemWidgets.splice(fromIndex, 1);
        this._itemWidgets.splice(toIndex, 0, itemWidget);

        const itemElement = itemWidget.getElement();
        const nextWidget = this._itemWidgets[toIndex + 1];
        if (nextWidget) {
          this._listContainerElement.insertBefore(itemElement, nextWidget.getElement());
        } else {
          this._listContainerElement.appendChild(itemElement);
        }

        this._refreshBadges();
        this._updateGroupSeparators();
        this.emit(context.constants.widgetEvents.listReorder, { orderedIds: this.getOrderedIds() });
        return true;
      },

      /**
       * Set the currently hovered item and update CSS drop indicators.
       * @param {?classes.FilterableListItemWidget} itemWidget
       * @param {boolean} [isAbove]
       * @private
       */
      _setHoveredItem: function (itemWidget, isAbove) {
        if (this._hoveredItem) {
          this._hoveredItem.toggleClass("dragover-above", false);
          this._hoveredItem.toggleClass("dragover-below", false);
        }
        this._hoveredItem = itemWidget;
        this._dropAbove = Boolean(isAbove);
        if (this._hoveredItem) {
          this._hoveredItem.toggleClass("dragover-above", this._dropAbove);
          this._hoveredItem.toggleClass("dragover-below", !this._dropAbove);
        }
      },

      /**
       * Remove all drag state and document listeners.
       * @private
       */
      _cleanupDrag: function () {
        if (this._draggedItem) {
          this._draggedItem.toggleClass("dragging", false);
        }
        this._setHoveredItem(null);
        this._draggedItem = null;
        this._dropAbove = false;
        document.removeEventListener("pointermove", this._boundOnPointerMove);
        document.removeEventListener("pointerup", this._boundOnPointerUp);
      },

      /**
       * Find the item widget that owns the given DOM element.
       * @param {HTMLElement} element - a .gbc_FilterableListItemWidget element
       * @returns {?classes.FilterableListItemWidget}
       * @private
       */
      _getItemWidgetFromElement: function (element) {
        for (const itemWidget of this._itemWidgets) {
          if (itemWidget.getElement() === element) {
            return itemWidget;
          }
        }
        return null;
      },

      // ── Keyboard navigation (internal) ──────────────────────────────────────

      /**
       * Keyboard interaction of a list row, following the ARIA layout grid pattern:
       * Up/Down move between rows, Left/Right between the row stops, Space toggles
       * visibility, Alt+Up/Down move the row, Home/End jump to the first/last row,
       * Enter activates the focused stop (position edit, visibility toggle).
       * Reached through the GBC key pipeline: the focused item widget delegates here.
       * @param {classes.FilterableListItemWidget} itemWidget - the row that received the key
       * @param {string} keyString - normalized key
       * @param {KeyboardEvent} domKeyEvent
       * @returns {boolean} true when the key was handled
       */
      manageItemKeyDown: function (itemWidget, keyString, domKeyEvent) {
        if (this._editingItemWidget === itemWidget) {
          return this._managePositionEditKeyDown(keyString);
        }
        switch (keyString) {
          case "up":
          case "down": {
            const delta = keyString === "up" ? -1 : 1;
            if (domKeyEvent.altKey) {
              this._moveItem(itemWidget, delta);
            } else {
              this._focusSibling(itemWidget, delta);
            }
            return true;
          }
          case "left":
          case "right":
            this._moveCellFocus(itemWidget, keyString === "right" ? 1 : -1);
            return true;
          case "home":
            this._focusItemWidget(this._getDisplayedItemWidgets()[0]);
            return true;
          case "end": {
            this._focusItemWidget(this._getDisplayedItemWidgets().at(-1));
            return true;
          }
          case "space":
            this._toggleItemVisibility(itemWidget);
            return true;
          case "enter":
          case "return":
            if (document.activeElement === itemWidget.getVisibilityButtonElement()) {
              this._toggleItemVisibility(itemWidget);
            } else {
              this._startPositionEdit(itemWidget);
            }
            return true;
          default:
            return false;
        }
      },

      /**
       * Toggle the item visibility, announcing the refusal on a non-hidable item —
       * silence reads as a failure
       * @param {classes.FilterableListItemWidget} itemWidget
       * @private
       */
      _toggleItemVisibility: function (itemWidget) {
        if (itemWidget.isHidable()) {
          itemWidget.toggleVisibility();
        } else {
          this.announce(gbc.I18NService.t(itemWidget.isItemVisible() ? "gwc.filterableList.cantHide" : "gwc.filterableList.cantShow"));
        }
      },

      /**
       * Move the focus to the previous/next stop of the row: the row itself,
       * the position input, then the visibility button (layout grid pattern)
       * @param {classes.FilterableListItemWidget} itemWidget
       * @param {number} delta - 1 for right, -1 for left
       * @private
       */
      _moveCellFocus: function (itemWidget, delta) {
        const stops = this._getRowFocusStops(itemWidget);
        const target = stops[stops.indexOf(document.activeElement) + delta];
        if (target) {
          this._focusRowStop(itemWidget, target);
        }
      },

      /**
       * Returns the focusable stops of a row, in visual order — hidden badges are skipped
       * @param {classes.FilterableListItemWidget} itemWidget
       * @returns {Array<HTMLElement>}
       * @private
       */
      _getRowFocusStops: function (itemWidget) {
        const stops = [itemWidget.getElement()];
        const badge = itemWidget.getBadgeElement();
        if (badge && badge.offsetParent !== null) {
          stops.push(badge);
        }
        stops.push(itemWidget.getVisibilityButtonElement());
        return stops;
      },

      /**
       * Focus a row stop as a grid navigation move — the badge stays readonly
       * @param {classes.FilterableListItemWidget} itemWidget
       * @param {HTMLElement} stopElement
       * @private
       */
      _focusRowStop: function (itemWidget, stopElement) {
        this._setRovingItem(itemWidget);
        this._gridNavFocus = true;
        stopElement.focus();
        this._gridNavFocus = false;
      },

      /**
       * Keys while a position input is edited: Enter confirms, Escape cancels.
       * Anything else stays native so typing works — Escape handled here also
       * keeps the hosting side panel open.
       * @param {string} keyString - normalized key
       * @returns {boolean} true when the key was handled
       * @private
       */
      _managePositionEditKeyDown: function (keyString) {
        switch (keyString) {
          case "enter":
          case "return":
            this._confirmPositionEdit();
            return true;
          case "esc":
            this._cancelPositionEdit(true);
            return true;
          default:
            return false;
        }
      },

      /**
       * Enter position-edit mode: the badge input of the item becomes editable and focused.
       * Refused on an unmovable item — announced instead, as the drag handle tooltip does.
       * @param {classes.FilterableListItemWidget} itemWidget
       * @private
       */
      _startPositionEdit: function (itemWidget) {
        if (this._editingItemWidget === itemWidget) {
          return;
        }
        const input = itemWidget.getBadgeElement();
        if (!itemWidget.isMovable()) {
          this.announce(gbc.I18NService.t("gwc.filterableList.cantMove"));
          // a click on the locked badge must not leave the focus on it: back to the row
          if (document.activeElement === input) {
            itemWidget.domFocus();
          }
          return;
        }
        this._editingItemWidget = itemWidget;
        this._editOriginalValue = input.value;
        // the editing class reveals the input even when the position badges are hidden
        itemWidget.toggleClass("position-editing", true);
        input.readOnly = false;
        input.focus();
        input.select();
      },

      /**
       * Confirm the position edit: parse, clamp and apply the typed position,
       * then put the focus back on the row. A non-numeric value cancels.
       * @private
       */
      _confirmPositionEdit: function () {
        const itemWidget = this._editingItemWidget;
        const position = Number.parseInt(itemWidget.getBadgeElement().value, 10);
        if (Number.isNaN(position)) {
          this._cancelPositionEdit(true);
          return;
        }
        this._endPositionEdit();
        this._moveItemToPosition(itemWidget, position);
        this._focusItemWidget(itemWidget);
      },

      /**
       * Cancel the position edit, restoring the displayed position.
       * @param {boolean} refocus - true to restore the grid navigation focus (Escape); false when the focus already left (blur)
       * @private
       */
      _cancelPositionEdit: function (refocus) {
        const itemWidget = this._editingItemWidget;
        this._endPositionEdit();
        this.announce(
          gbc.I18NService.t("gwc.filterableList.positionEditCancelledAnnounce")
            .replace("{name}", itemWidget.getItemData().label)
            .replace("{position}", String(this._itemWidgets.indexOf(itemWidget) + 1)),
          true
        );
        if (refocus) {
          // Escape leaves edit mode but not the cell: back to the readonly badge when it stays visible
          const badge = itemWidget.getBadgeElement();
          if (badge && badge.offsetParent !== null) {
            this._focusRowStop(itemWidget, badge);
          } else {
            this._focusItemWidget(itemWidget);
          }
        }
      },

      /**
       * Leave position-edit mode, restoring the input readonly state and displayed value.
       * @private
       */
      _endPositionEdit: function () {
        if (!this._editingItemWidget) {
          return;
        }
        const input = this._editingItemWidget.getBadgeElement();
        input.value = this._editOriginalValue;
        input.readOnly = true;
        this._editingItemWidget.toggleClass("position-editing", false);
        this._editingItemWidget = null;
        this._editOriginalValue = "";
      },

      /**
       * Move an item to a 1-based position, clamped to its group zone.
       * The outcome is announced assertively, as the position input confirmation requires.
       * @param {classes.FilterableListItemWidget} itemWidget
       * @param {number} position - requested position, as displayed on the badges
       * @private
       */
      _moveItemToPosition: function (itemWidget, position) {
        if (!itemWidget.isMovable()) {
          this.announce(gbc.I18NService.t("gwc.filterableList.cantMove"));
          return;
        }
        // groups are reorder boundaries: out-of-range positions are clamped to the item zone
        const group = itemWidget.getGroup();
        let firstIndex = -1;
        let lastIndex = -1;
        for (let i = 0; i < this._itemWidgets.length; i++) {
          if (this._itemWidgets[i].getGroup() === group) {
            if (firstIndex === -1) {
              firstIndex = i;
            }
            lastIndex = i;
          }
        }
        const toIndex = Math.min(Math.max(position - 1, firstIndex), lastIndex);

        if (this._applyReorder(itemWidget, toIndex)) {
          this.announce(
            gbc.I18NService.t("gwc.filterableList.itemMovedToAnnounce")
              .replace("{name}", itemWidget.getItemData().label)
              .replace("{position}", String(toIndex + 1)),
            true
          );
        } else {
          this.announce(
            gbc.I18NService.t("gwc.filterableList.positionEditCancelledAnnounce")
              .replace("{name}", itemWidget.getItemData().label)
              .replace("{position}", String(this._itemWidgets.indexOf(itemWidget) + 1)),
            true
          );
        }
      },

      /**
       * Sync the roving tab stop when a row gains focus (a mouse click on a row seeds the keyboard
       * position); a mouse click on a badge input starts its position edit — a grid navigation
       * move does not, editing is entered with Enter.
       * @param {FocusEvent} evt
       * @private
       */
      _onListFocusIn: function (evt) {
        const itemElement = evt.target.closest(".gbc_FilterableListItemWidget");
        const itemWidget = itemElement ? this._getItemWidgetFromElement(itemElement) : null;
        if (!itemWidget) {
          return;
        }
        if (evt.target === itemWidget.getBadgeElement() && !this._gridNavFocus) {
          this._startPositionEdit(itemWidget);
        }
        this._setRovingItem(itemWidget);
      },

      /**
       * A blur of the edited position input cancels the edit — Enter is the only confirmation
       * @param {FocusEvent} evt
       * @private
       */
      _onListFocusOut: function (evt) {
        if (evt.target === this._editingItemWidget?.getBadgeElement()) {
          this._cancelPositionEdit(false);
        }
      },

      /**
       * Returns the item widgets currently displayed (not filtered out), in visual order
       * @returns {Array<classes.FilterableListItemWidget>}
       * @private
       */
      _getDisplayedItemWidgets: function () {
        return this._itemWidgets.filter((widget) => !widget.hasClass("filtered-out"));
      },

      /**
       * Put the single list tab stop on the given item
       * @param {?classes.FilterableListItemWidget} itemWidget
       * @private
       */
      _setRovingItem: function (itemWidget) {
        for (const widget of this._itemWidgets) {
          widget.getElement().tabIndex = widget === itemWidget ? 0 : -1;
        }
        this._rovingItemId = itemWidget?.getItemData().id ?? null;
      },

      /**
       * Keep the tab stop on a displayed item, preferring the current one
       * @private
       */
      _updateRovingTabStop: function () {
        const displayed = this._getDisplayedItemWidgets();
        let target = displayed.find((widget) => widget.getItemData().id === this._rovingItemId);
        if (!target) {
          target = displayed[0];
        }
        this._setRovingItem(target || null);
      },

      /**
       * Move the tab stop to the given item and focus it
       * @param {classes.FilterableListItemWidget} [itemWidget]
       * @private
       */
      _focusItemWidget: function (itemWidget) {
        if (!itemWidget) {
          return;
        }
        this._setRovingItem(itemWidget);
        itemWidget.domFocus();
      },

      /**
       * Focus an item by id, falling back to the first displayed item
       * @param {string} id
       * @private
       */
      _focusItemById: function (id) {
        const itemWidget = this.getItemWidget(id);
        const displayed = this._getDisplayedItemWidgets();
        this._focusItemWidget(itemWidget && displayed.includes(itemWidget) ? itemWidget : displayed[0]);
      },

      /**
       * Focus the previous/next displayed item
       * @param {classes.FilterableListItemWidget} itemWidget
       * @param {number} delta - -1 for previous, 1 for next
       * @private
       */
      _focusSibling: function (itemWidget, delta) {
        const displayed = this._getDisplayedItemWidgets();
        this._focusItemWidget(displayed[displayed.indexOf(itemWidget) + delta]);
      },

      /**
       * Move an item one displayed position up or down, as dropping it over its neighbor would
       * @param {classes.FilterableListItemWidget} itemWidget
       * @param {number} delta - -1 to move up, 1 to move down
       * @private
       */
      _moveItem: function (itemWidget, delta) {
        if (!itemWidget.isMovable()) {
          this.announce(gbc.I18NService.t("gwc.filterableList.cantMove"));
          return;
        }
        const displayed = this._getDisplayedItemWidgets();
        const targetWidget = displayed[displayed.indexOf(itemWidget) + delta];
        // groups are reorder boundaries: an item cannot move into another group
        if (!targetWidget || targetWidget.getGroup() !== itemWidget.getGroup()) {
          this.announce(gbc.I18NService.t("gwc.filterableList.itemNotMovedAnnounce").replace("{name}", itemWidget.getItemData().label));
          return;
        }
        this._reorderItem(itemWidget, targetWidget, delta < 0);
        // Moving the focused row in the DOM can drop the focus — take it back
        this._focusItemWidget(itemWidget);
      },

      /**
       * Register a handler for list reorder events
       * @param {Function} hook - receives { orderedIds: Array<string> }
       * @returns {HandleRegistration}
       */
      onReorder: function (hook) {
        return this.when(context.constants.widgetEvents.listReorder, hook);
      },

      /**
       * Register a handler for visibility toggle events
       * @param {Function} hook - receives { id: string }
       * @returns {HandleRegistration}
       */
      onVisibilityToggle: function (hook) {
        return this.when(context.constants.widgetEvents.listItemVisibility, hook);
      },

      /**
       * Register a handler for show all events
       * @param {Function} hook
       * @returns {HandleRegistration}
       */
      onShowAll: function (hook) {
        return this.when(context.constants.widgetEvents.listShowAll, hook);
      }
    };
  });
});
